import { Tabs, type TabsProps } from 'antd';
import { CanceledError } from 'axios';
import { Formik } from 'formik';
import { useCallback, useContext, useEffect, useMemo } from 'react';

import './CustomerLeadStepProductSpecifications.scss';

import { customersApi } from 'src/app/services/Customers/Customers.service';
import type { ICustomerLeadProductDetailsTabFormValues } from 'src/components/customers/CustomerLeadTypes';
import { toCustomerLeadProductCreateOrUpdateSpecificationData } from 'src/components/customers/CustomerLeadValidation';
import { CustomerLeadStepFieldsTab } from 'src/components/customers/steps/CustomerLeadStepFieldsTab';
import { CustomerLeadStepLayout } from 'src/components/customers/steps/CustomerLeadStepLayout';
import {
  customerLeadProductProductSpecificationsInitialValues,
  customerLeadProductProductSpecificationsValidationSchema,
} from 'src/components/customers/steps/ProductSpecifications/CustomerLeadStepProductSpecifications.validation';
import { CustomersContext } from 'src/pages/CustomersPage/CustomersPage.context';
import { ProductSpecificationFieldGroup } from 'src/types/reference/ProductSpecificationFieldGroup';

type CustomerLeadStepProductSpecificationsProps = {
  customerProductLeadId: string;
};

export function CustomerLeadStepProductSpecifications({
  customerProductLeadId,
}: Readonly<CustomerLeadStepProductSpecificationsProps>) {
  const {
    isEditing,
    customerLeadProduct,
    customerLeadProductSpecification,
    updateCustomerLeadProduct,
    setCustomerStepGuideKey,
  } = useContext(CustomersContext);

  const [, partialUpdateCustomerLeadProduct, cancelPartialUpdateCustomerLeadProduct] =
    customersApi.usePartialUpdateCustomerProductLeadMutation(customerProductLeadId);

  const save = useCallback(
    async (newValues: ICustomerLeadProductDetailsTabFormValues, submit: boolean) => {
      if (!customerLeadProductSpecification) {
        throw new Error('Product specification is not found');
      }

      cancelPartialUpdateCustomerLeadProduct();

      try {
        const updatedCustomerProductLeadData = await partialUpdateCustomerLeadProduct({
          data: {
            fieldGroupData: {
              fieldGroup: ProductSpecificationFieldGroup.ProductDetails,
              specData: toCustomerLeadProductCreateOrUpdateSpecificationData(
                newValues.specData,
                customerLeadProductSpecification,
                ProductSpecificationFieldGroup.ProductDetails
              ),

              submit,
            },
          },
        });

        if (submit) {
          updateCustomerLeadProduct(updatedCustomerProductLeadData.data);
        }
      } catch (unknownError) {
        if (unknownError instanceof CanceledError) {
          console.debug('ProductSpecifications: auto save request has been cancelled');
        } else {
          console.error(unknownError);
        }
      }
    },
    [
      customerLeadProductSpecification,
      cancelPartialUpdateCustomerLeadProduct,
      partialUpdateCustomerLeadProduct,
      updateCustomerLeadProduct,
    ]
  );

  const handleValuesChange = useCallback(
    (saveValues: ICustomerLeadProductDetailsTabFormValues) => save(saveValues, false),
    [save]
  );

  const tabs: Exclude<TabsProps['items'], undefined> = useMemo(
    () => [
      {
        key: ProductSpecificationFieldGroup.ProductDetails,
        label: 'Product Details',
        disabled: isEditing,
        children: customerLeadProductSpecification && (
          <CustomerLeadStepFieldsTab
            fieldGroup={ProductSpecificationFieldGroup.ProductDetails}
            specification={customerLeadProductSpecification}
            onChange={handleValuesChange}
          />
        ),
      },
    ],
    [isEditing, customerLeadProductSpecification, handleValuesChange]
  );

  const handleSubmit = useCallback(
    async (submitValues: ICustomerLeadProductDetailsTabFormValues) => {
      await save(submitValues, true);

      return true;
    },
    [save]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    setCustomerStepGuideKey(ProductSpecificationFieldGroup.ProductDetails);
  }, []);

  if (!customerLeadProduct || !customerLeadProductSpecification) {
    return null;
  }

  return (
    <Formik<ICustomerLeadProductDetailsTabFormValues>
      // Load whole specData object (do not filter by group). There are some problems with form re-initialization.
      // In Description tab (2nd tab) there are empty values
      initialValues={customerLeadProductProductSpecificationsInitialValues(
        customerLeadProductSpecification.fields,
        customerLeadProduct
      )}
      validationSchema={customerLeadProductProductSpecificationsValidationSchema(
        customerLeadProductSpecification.fields
      )}
      validateOnMount={true}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      <CustomerLeadStepLayout>
        <div className="customer-lead-step-product-specifications__container">
          <Tabs
            items={tabs}
            animated={false}
            destroyInactiveTabPane={false}
            activeKey={ProductSpecificationFieldGroup.ProductDetails}
          />
        </div>
      </CustomerLeadStepLayout>
    </Formik>
  );
}
